/**
 * GroupingLeadersCard — who may look at this grouping's overview.
 *
 * A leader is an `admin_scopes` row on the grouping (or anything above it), so
 * granting access here reuses the same RBAC the rest of the platform reads.
 * Share links are read-only, expire, and never show giving figures.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, UserPlus, Trash2, Link2, Copy, Shield } from 'lucide-react';

export const GroupingLeadersCard = ({ churchId }) => {
  const qc = useQueryClient();
  const [email, setEmail] = React.useState('');
  const [freshLink, setFreshLink] = React.useState(null);

  const { data: leaderData, isLoading } = useQuery({
    queryKey: ['grouping-leaders', churchId],
    queryFn: () => api.get(`/admin/churches/${churchId}/leaders`).then((r) => r.data),
  });
  const { data: linkData } = useQuery({
    queryKey: ['grouping-share-links', churchId],
    queryFn: () => api.get(`/admin/churches/${churchId}/overview/share`).then((r) => r.data),
    retry: false,
  });

  const add = useMutation({
    mutationFn: () => api.post(`/admin/churches/${churchId}/leaders`,
      { email: email.trim(), role: 'viewer' }),
    onSuccess: () => {
      toast.success('Leader can now open this overview');
      setEmail('');
      qc.invalidateQueries({ queryKey: ['grouping-leaders', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (userId) => api.delete(`/admin/churches/${churchId}/leaders/${userId}`),
    onSuccess: () => {
      toast.success('Access removed');
      qc.invalidateQueries({ queryKey: ['grouping-leaders', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const mintLink = useMutation({
    mutationFn: () => api.post(`/admin/churches/${churchId}/overview/share`),
    onSuccess: (r) => {
      setFreshLink(`${window.location.origin}${r.data.path}`);
      toast.success('Share link created — copy it now, it is shown once');
      qc.invalidateQueries({ queryKey: ['grouping-share-links', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const revokeLink = useMutation({
    mutationFn: (id) => api.delete(`/admin/churches/${churchId}/overview/share/${id}`),
    onSuccess: () => {
      toast.success('Link revoked');
      setFreshLink(null);
      qc.invalidateQueries({ queryKey: ['grouping-share-links', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const leaders = leaderData?.leaders || [];
  const links = linkData?.links || [];

  return (
    <Card data-testid="grouping-leaders-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-slate-600" /> Regional leaders
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Leaders you add here can open this overview — and nothing else in admin.
          They must already have an admin account.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="leader@example.com" className="max-w-xs"
                 data-testid="grouping-leader-email" />
          <Button size="sm" onClick={() => add.mutate()}
                  disabled={!email.trim() || add.isPending}
                  data-testid="grouping-leader-add">
            {add.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                           : <UserPlus className="h-4 w-4 mr-1" />}
            Give access
          </Button>
        </div>

        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        ) : leaders.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="grouping-leaders-empty">
            No regional leaders yet — only platform admins can see this page.
          </p>
        ) : (
          <div className="border rounded-lg divide-y" data-testid="grouping-leaders-list">
            {leaders.map((l) => (
              <div key={l.user_id} className="p-3 flex items-center justify-between gap-3"
                   data-testid={`grouping-leader-${l.user_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {l.name || l.email || l.user_id}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{l.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="text-[10px] capitalize">{l.role}</Badge>
                  <Button variant="outline" size="sm"
                          className="h-7 px-2 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                          onClick={() => remove.mutate(l.user_id)}
                          data-testid={`grouping-leader-remove-${l.user_id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pt-2 border-t space-y-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              Or share a read-only link (30 days, no giving figures, no login).
            </p>
            <Button variant="outline" size="sm" onClick={() => mintLink.mutate()}
                    disabled={mintLink.isPending} data-testid="grouping-share-create">
              {mintLink.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  : <Link2 className="h-4 w-4 mr-1" />}
              New share link
            </Button>
          </div>

          {freshLink && (
            <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded"
                 data-testid="grouping-share-fresh">
              <code className="text-xs text-emerald-900 truncate flex-1">{freshLink}</code>
              <Button variant="ghost" size="sm" className="h-7"
                      onClick={() => {
                        navigator.clipboard.writeText(freshLink);
                        toast.success('Link copied');
                      }}
                      data-testid="grouping-share-copy">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {links.length > 0 && (
            <div className="space-y-1" data-testid="grouping-share-list">
              {links.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-3 text-xs text-slate-600"
                     data-testid={`grouping-share-${l.id}`}>
                  <span>
                    Created {new Date(l.created_at).toLocaleDateString()} ·{' '}
                    {l.expired ? 'expired' : `expires ${new Date(l.expires_at).toLocaleDateString()}`} ·{' '}
                    {l.views} view{l.views === 1 ? '' : 's'}
                  </span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-rose-600"
                          onClick={() => revokeLink.mutate(l.id)}
                          data-testid={`grouping-share-revoke-${l.id}`}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GroupingLeadersCard;
